import { ETSY_SHOP_KEY_PREFIX } from '../auth/menu-access';

/**
 * Közös helper-ek a Felhasználók képernyőhöz (lista + szerkesztő modál).
 * Forrás: erp-design prototípus — 23-logic.js usersVals() + navList.
 */

/** Nav menüpontok a designból (navList a 23-logic.js-ben) — a menü-jogosultság kulcsai. */
export const NAV_MENU_ITEMS: string[] = [
  'Mai nap',
  'Komponensek',
  'Készlet',
  'BGFox termékek',
  'Raktár szabály',
  'Gyártás',
  'Beszerzés',
  'Anyagok',
  'Rendelések',
  'Számlázás',
  'Felhasználók',
  'Szállítók',
  'Futár rendelés',
  'Tracking',
  'Support',
  'Levelezés',
  'Munkaidő',
  'Munkaidő admin',
  'Riportok',
  'Etsy shopok',
  'Beállítások',
];

/** Csapatchat-tagság: ugyanabban a menü-térképben tárolódik („Chat"), de a szerkesztőben külön kapcsoló. */
export const CHAT_MENU = 'Chat';

/** Foxi oldala (időzített feladatok, emlékek, költés): külön kapcsoló, kulcs nélkül csak admin. */
export const FOXI_MENU = 'Foxi';

/** Boris oldala (tudástár, a tervezőgép állapota): külön kapcsoló, kulcs nélkül csak admin. */
export const BORIS_MENU = 'Boris';
/** Farmy oldala (tudástár, a mini PC állapota): külön kapcsoló, kulcs nélkül csak admin. */
export const FARMY_MENU = 'Farmy';

/** A „Beállítások" kulcs nélkül csak adminnak látható — API-kulcsok, jelszavak vannak mögötte. */
export const ADMIN_DEFAULT_MENU = 'Beállítások';

/** Etsy shopok oldala (/merchants/etsy): kulcs nélkül csak adminnak. */
export const ETSY_MENU = 'Etsy shopok';

/** Szerep → szín (roleColors a 23-logic.js-ben), kulcs kisbetűsítve. */
const ROLE_COLORS: { [key: string]: string } = {
  'admin': '#9A6E9E',
  'gyártás': '#5E7FA8',
  'gyartas': '#5E7FA8',
  'csomagolás': '#8A6A28',
  'csomagolas': '#8A6A28',
  'support': '#6E9663',
};

/** Szín a szerep címkéjéhez; ismeretlen szerepre null (hívó ad fallbacket, mint a prototípus). */
export function roleColor(label: string | null | undefined): string | null {
  if (!label) {
    return null;
  }
  return ROLE_COLORS[label.trim().toLowerCase()] || null;
}

/** Monogram a névből (max 2 karakter) — a prototípus initials logikája. */
export function initialsOf(name: string | null | undefined): string {
  return (name || '')
    .trim()
    .split(/\s+/)
    .filter(w => !!w)
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/**
 * A user `menus` mezőjének normalizálása teljes térképpé.
 * Hiányzó mező / hiányzó kulcs = látható menü (prototípus: `menus[m] !== false`).
 * JSON stringként érkező értéket is kezel (backend párhuzamosan készül).
 */
export function normalizeMenus(raw: unknown, isAdmin = false): { [menu: string]: boolean } {
  let obj: any = raw;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      obj = null;
    }
  }
  if (!obj || typeof obj !== 'object') {
    obj = null;
  }
  const menus: { [menu: string]: boolean } = {};
  for (const m of [...NAV_MENU_ITEMS, CHAT_MENU, FOXI_MENU, BORIS_MENU, FARMY_MENU]) {
    const explicit = obj && typeof obj[m] === 'boolean' ? (obj[m] as boolean) : null;
    // Kulcs nélkül: a Beállítások és a csapatchat csak adminnak, minden más mindenkinek látható.
    menus[m] = explicit !== null ? explicit : (m === ADMIN_DEFAULT_MENU || m === ETSY_MENU || m === CHAT_MENU || m === FOXI_MENU || m === BORIS_MENU || m === FARMY_MENU ? isAdmin : true);
  }
  // Etsy shop-szűrő kulcsai (`Etsy shop:<id>`) — megőrizzük, különben mentéskor elvesznének.
  if (obj) {
    for (const key of Object.keys(obj)) {
      if (key.startsWith(ETSY_SHOP_KEY_PREFIX) && typeof obj[key] === 'boolean') {
        menus[key] = obj[key];
      }
    }
  }
  return menus;
}

/** Új felhasználó alap menü-térképe: minden igen, kivéve „Munkaidő admin" és „Beállítások"; csapatchat: nem. */
export function defaultMenusForNewUser(): { [menu: string]: boolean } {
  const menus: { [menu: string]: boolean } = {};
  for (const m of NAV_MENU_ITEMS) {
    menus[m] = m !== 'Munkaidő admin' && m !== ADMIN_DEFAULT_MENU && m !== ETSY_MENU;
  }
  menus[CHAT_MENU] = false;
  menus[FOXI_MENU] = false;
  menus[BORIS_MENU] = false;
  menus[FARMY_MENU] = false;
  return menus;
}
